import { useMemo } from 'react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import type { PortfolioSnapshot, PriceBar } from '../lib/supabase'

interface Props {
  snapshots: PortfolioSnapshot[]
  benchmarkPrices?: PriceBar[]
  periodLabel?: string
}

interface ChartPoint extends PortfolioSnapshot {
  rsp_equivalent: number | null
  rsp_return: number | null
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data: ChartPoint = payload[0].payload
    return (
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.8rem',
      }}>
        <div style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-sm)' }}>
          {format(parseISO(data.snapshot_date), 'MMM d, yyyy')}
        </div>
        <div style={{ color: 'var(--accent-cyan)', fontWeight: 600 }}>
          Portfolio: {formatCurrency(data.net_worth)}
        </div>
        <div style={{
          color: data.daily_pnl >= 0 ? 'var(--positive)' : 'var(--negative)',
          fontSize: '0.75rem',
        }}>
          {data.daily_pnl >= 0 ? '+' : ''}{formatCurrency(data.daily_pnl)} ({data.daily_pnl_pct.toFixed(2)}%)
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: 'var(--space-xs)' }}>
          Drawdown: -{data.drawdown_pct.toFixed(2)}%
        </div>
        {data.rsp_equivalent != null && data.rsp_return != null && (
          <div style={{
            color: '#f59e0b',
            fontSize: '0.75rem',
            marginTop: 'var(--space-xs)',
            borderTop: '1px solid var(--border-subtle)',
            paddingTop: 'var(--space-xs)',
          }}>
            RSP (EW): {formatCurrency(data.rsp_equivalent)}
            {' '}({data.rsp_return >= 0 ? '+' : ''}{data.rsp_return.toFixed(2)}%)
          </div>
        )}
      </div>
    )
  }
  return null
}

export function PerformanceChart({ snapshots, benchmarkPrices, periodLabel = '30 Days' }: Props) {
  // Build date→close map from benchmark prices
  const benchmarkMap = useMemo(() => {
    if (!benchmarkPrices?.length) return {} as Record<string, number>
    return benchmarkPrices.reduce((acc, b) => { acc[b.date] = b.close; return acc }, {} as Record<string, number>)
  }, [benchmarkPrices])

  // Merge RSP values into chart data, normalized to portfolio start value
  const chartData = useMemo((): ChartPoint[] => {
    if (!snapshots.length) return []
    const startNetWorth = snapshots[0].net_worth

    // Find RSP price at or nearest-prior to period start date
    let rspStart: number | null = null
    const startDate = snapshots[0].snapshot_date
    if (benchmarkMap[startDate]) {
      rspStart = benchmarkMap[startDate]
    } else {
      // Scan backward — carry prior trading day price when period starts on holiday/weekend
      const sortedDates = Object.keys(benchmarkMap).sort()
      for (let i = sortedDates.length - 1; i >= 0; i--) {
        if (sortedDates[i] <= startDate) {
          rspStart = benchmarkMap[sortedDates[i]]
          break
        }
      }
    }

    // Track last known RSP price for gap-filling (holidays/weekends)
    let lastRsp: number | null = rspStart

    return snapshots.map(s => {
      if (benchmarkMap[s.snapshot_date]) lastRsp = benchmarkMap[s.snapshot_date]
      const rsp_equivalent = (rspStart && lastRsp)
        ? startNetWorth * (lastRsp / rspStart)
        : null
      const rsp_return = (rspStart && lastRsp)
        ? ((lastRsp / rspStart) - 1) * 100
        : null
      return { ...s, rsp_equivalent, rsp_return }
    })
  }, [snapshots, benchmarkMap])

  if (snapshots.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Performance</h2>
        </div>
        <div className="empty-state">
          <div className="empty-state-text">No performance data</div>
        </div>
      </div>
    )
  }

  const startValue = snapshots[0]?.net_worth || 0
  const endValue = snapshots[snapshots.length - 1]?.net_worth || 0
  const totalReturn = ((endValue - startValue) / startValue) * 100

  const hasRSP = chartData.some(d => d.rsp_equivalent != null)
  const rspEndReturn = chartData[chartData.length - 1]?.rsp_return

  // Domain includes both portfolio and RSP values
  const allValues = [
    ...chartData.map(d => d.net_worth),
    ...chartData.filter(d => d.rsp_equivalent != null).map(d => d.rsp_equivalent as number),
  ]
  const minValue = Math.min(...allValues) * 0.98
  const maxValue = Math.max(...allValues) * 1.02

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Performance — {periodLabel}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasRSP && rspEndReturn != null && (
            <span className="card-badge" title="vs period start" style={{
              background: rspEndReturn >= 0 ? 'rgba(245, 158, 11, 0.15)' : 'var(--negative-bg)',
              color: rspEndReturn >= 0 ? '#f59e0b' : 'var(--negative)',
              fontSize: '0.7rem',
            }}>
              RSP {rspEndReturn >= 0 ? '+' : ''}{rspEndReturn.toFixed(2)}%
            </span>
          )}
          <span className="card-badge" style={{
            background: totalReturn >= 0 ? 'var(--positive-bg)' : 'var(--negative-bg)',
            color: totalReturn >= 0 ? 'var(--positive)' : 'var(--negative)',
          }}>
            {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%
          </span>
        </div>
      </div>

      {hasRSP && (
        <div style={{
          display: 'flex',
          gap: 16,
          paddingLeft: 'var(--space-md)',
          paddingBottom: 'var(--space-xs)',
          fontFamily: 'var(--font-mono)',
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 2, background: 'var(--accent-cyan)', display: 'inline-block', borderRadius: 1 }} />
            Portfolio
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 2, background: '#f59e0b', display: 'inline-block', borderRadius: 1, opacity: 0.8 }} />
            RSP (EW S&P 500)
          </span>
        </div>
      )}

      <div className="chart-container" style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--accent-cyan)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--accent-cyan)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--border-subtle)"
              vertical={false}
            />
            <XAxis
              dataKey="snapshot_date"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
              tickFormatter={(date) => format(parseISO(date), 'M/d')}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[minValue, maxValue]}
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={startValue}
              stroke="var(--text-dim)"
              strokeDasharray="3 3"
            />
            <Area
              type="monotone"
              dataKey="net_worth"
              stroke="var(--accent-cyan)"
              strokeWidth={2}
              fill="url(#netWorthGradient)"
              dot={false}
              activeDot={{
                r: 4,
                fill: 'var(--accent-cyan)',
                stroke: 'var(--bg-card)',
                strokeWidth: 2,
              }}
            />
            {hasRSP && (
              <Line
                type="monotone"
                dataKey="rsp_equivalent"
                stroke="#f59e0b"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={{
                  r: 3,
                  fill: '#f59e0b',
                  stroke: 'var(--bg-card)',
                  strokeWidth: 2,
                }}
                connectNulls={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
